import React, { useState, useEffect } from 'react';
import {
  Plus, Search, Pencil, Trash2, Eye, EyeOff, X,
  Loader2, FileText
} from 'lucide-react';
import { useAdmin } from './AdminContext';

const CATEGORIES = [
  'EPR: E-Waste & Sustainability',
  'Corporate IT & Data Security',
  'OEM & Reverse Supply Chain',
  'Technology & Innovation',
  'Community & Awareness',
];

export default function PostsPage() {
  const { token, apiUrl, authHeaders } = useAdmin();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({ title: '', category: CATEGORIES[0], excerpt: '', content: '', image_url: '', author: 'Grenew Team' });
  const [saving, setSaving] = useState(false);

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${apiUrl}/api/blog/posts?published_only=false`);
      const data = await res.json();
      setPosts(data.filter(p => p.source !== 'rss'));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchPosts(); }, []);

  const openNew = () => {
    setEditingPost(null);
    setForm({ title: '', category: CATEGORIES[0], excerpt: '', content: '', image_url: '', author: 'Grenew Team' });
    setShowForm(true);
  };

  const openEdit = (post) => {
    setEditingPost(post);
    setForm({
      title: post.title,
      category: post.category,
      excerpt: post.excerpt,
      content: post.content || '',
      image_url: post.image_url || '',
      author: post.author || 'Grenew Team',
    });
    setShowForm(true);
  };

  const savePost = async () => {
    setSaving(true);
    try {
      if (editingPost) {
        await fetch(`${apiUrl}/api/blog/posts/${editingPost.id}`, {
          method: 'PUT',
          headers: authHeaders(),
          body: JSON.stringify(form),
        });
      } else {
        await fetch(`${apiUrl}/api/blog/posts`, {
          method: 'POST',
          headers: authHeaders(),
          body: JSON.stringify(form),
        });
      }
      setShowForm(false);
      fetchPosts();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    await fetch(`${apiUrl}/api/blog/posts/${id}`, { method: 'DELETE', headers: authHeaders() });
    fetchPosts();
  };

  const togglePublish = async (post) => {
    await fetch(`${apiUrl}/api/blog/posts/${post.id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ published: !post.published }),
    });
    fetchPosts();
  };

  const filtered = posts.filter(p => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return p.title?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q);
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 text-[#4fd1c5] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-posts-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-white">Blog Posts</h2>
          <p className="text-gray-500 text-sm mt-0.5">{posts.length} manual posts</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search posts..."
              data-testid="posts-search"
              className="pl-9 pr-4 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white text-sm placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/40 w-52 transition-colors"
            />
          </div>
          <button
            onClick={openNew}
            data-testid="admin-new-post-btn"
            className="flex items-center gap-2 bg-[#4fd1c5] hover:bg-[#3ec4b8] text-[#0a1628] px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
          >
            <Plus className="w-4 h-4" /> New Post
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="admin-posts-table">
            <thead className="border-b border-white/[0.06]">
              <tr>
                <th className="text-left px-5 py-3.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Title</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider hidden md:table-cell">Category</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider hidden sm:table-cell">Date</th>
                <th className="text-left px-5 py-3.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="text-right px-5 py-3.5 text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {filtered.map(post => (
                <tr key={post.id} className="hover:bg-white/[0.02] transition-colors">
                  <td className="px-5 py-4 text-sm font-medium text-white max-w-[300px] truncate">{post.title}</td>
                  <td className="px-5 py-4 hidden md:table-cell">
                    <span className="text-xs text-gray-400 bg-white/[0.05] px-2.5 py-1 rounded-full">{post.category?.split(':')[0]}</span>
                  </td>
                  <td className="px-5 py-4 text-xs text-gray-500 hidden sm:table-cell">{post.date}</td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex px-2.5 py-1 text-[10px] font-bold rounded-full ${
                      post.published ? 'bg-green-500/15 text-green-400' : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {post.published ? 'LIVE' : 'DRAFT'}
                    </span>
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => togglePublish(post)}
                        title={post.published ? 'Unpublish' : 'Publish'}
                        className="p-2 text-gray-500 hover:text-[#4fd1c5] transition-colors rounded-lg hover:bg-white/[0.04]"
                      >
                        {post.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => openEdit(post)}
                        title="Edit"
                        className="p-2 text-gray-500 hover:text-blue-400 transition-colors rounded-lg hover:bg-white/[0.04]"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deletePost(post.id)}
                        title="Delete"
                        className="p-2 text-gray-500 hover:text-red-400 transition-colors rounded-lg hover:bg-white/[0.04]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length === 0 && (
          <div className="p-12 text-center">
            <FileText className="w-8 h-8 text-gray-700 mx-auto mb-2" />
            <p className="text-gray-500 text-sm">No posts found.</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" data-testid="admin-post-modal">
          <div className="bg-[#111827] border border-white/[0.08] rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-white/[0.06]">
              <h3 className="text-lg font-bold text-white">{editingPost ? 'Edit Post' : 'New Post'}</h3>
              <button onClick={() => setShowForm(false)} className="p-1.5 text-gray-400 hover:text-white rounded-lg hover:bg-white/[0.06]">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Title *</label>
                <input
                  value={form.title}
                  onChange={e => setForm({ ...form, title: e.target.value })}
                  data-testid="admin-post-title"
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/50 transition-colors"
                  placeholder="Post title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Category *</label>
                  <select
                    value={form.category}
                    onChange={e => setForm({ ...form, category: e.target.value })}
                    data-testid="admin-post-category"
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white focus:outline-none focus:border-[#4fd1c5]/50"
                  >
                    {CATEGORIES.map(c => <option key={c} value={c} className="bg-[#111827]">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-400 mb-1.5">Author</label>
                  <input
                    value={form.author}
                    onChange={e => setForm({ ...form, author: e.target.value })}
                    data-testid="admin-post-author"
                    className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/50 transition-colors"
                    placeholder="Author name"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Excerpt *</label>
                <textarea
                  value={form.excerpt}
                  onChange={e => setForm({ ...form, excerpt: e.target.value })}
                  rows={2}
                  data-testid="admin-post-excerpt"
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/50 resize-none transition-colors"
                  placeholder="Brief summary..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Content</label>
                <textarea
                  value={form.content}
                  onChange={e => setForm({ ...form, content: e.target.value })}
                  rows={6}
                  data-testid="admin-post-content"
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/50 resize-none transition-colors"
                  placeholder="Full article content..."
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-400 mb-1.5">Image URL</label>
                <input
                  value={form.image_url}
                  onChange={e => setForm({ ...form, image_url: e.target.value })}
                  data-testid="admin-post-image"
                  className="w-full px-4 py-3 bg-white/[0.04] border border-white/[0.08] rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-[#4fd1c5]/50 transition-colors"
                  placeholder="https://..."
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 p-5 border-t border-white/[0.06]">
              <button
                onClick={() => setShowForm(false)}
                className="px-5 py-2.5 text-sm font-medium text-gray-400 hover:text-white rounded-lg hover:bg-white/[0.04] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={savePost}
                disabled={saving || !form.title || !form.excerpt}
                data-testid="admin-post-save"
                className="flex items-center gap-2 bg-[#4fd1c5] hover:bg-[#3ec4b8] text-[#0a1628] px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingPost ? 'Update Post' : 'Create Post'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
